/**
 * Series generate-from-chats / writer checklist (#678 piece 5 of 5).
 * Propose, review, accept or skip. People, place, and plot snapshots
 * are offered even when no book is named. Book accept writes one Timeline
 * idea card — the same book object Series Notes reads — only when the
 * title is a real book name. Snapshot accepts write Series Notes.
 * Never starts a book. Never merges dupes.
 * After the motion the writer stays in Series Notes (#782). The
 * picker loads talks and note files independently so Series
 * Discovery with notes still appears (#784). This path does not
 * open book-level Book Notes or create a story.
 */

import { documentImportApi } from '../api/documentImport';
import { docListDetail } from './docListDetail';
import { seriesBookBoardApi } from '../api/seriesBookBoard';
import { sessionsApi } from '../api/sessions';
import { isRealBookName } from '../components/Chat/SeriesBookCardOffer';
import { partitionSessions, sessionCardSummary } from './chatSessions';
import { SERIES_DISCOVERY_LABEL } from './focusTemplates';
import { boardPayload, clampProposition, itemsFromBoard, mergeTimelineItems, newIdeaCard } from './seriesBookBoard';
import { newCardId } from './seriesSnapshots';

import {
  BOOK_ACCEPT_NOTE as BOOK_ACCEPT_NOTE_COPY,
  CHARACTER_DOSSIER_NOTE_LINE,
  CHARACTER_NOTE_SKETCH,
  CUSTOM_ACCEPT_NOTE as CUSTOM_ACCEPT_NOTE_COPY,
  FROM_CHATS_AND_DOCS_LEDE,
  MAKE_NOTES_FROM_CHATS_AND_DOCS,
  PLACE_ACCEPT_NOTE as PLACE_ACCEPT_NOTE_COPY,
  PLOT_ACCEPT_NOTE as PLOT_ACCEPT_NOTE_COPY,
  BOOK_NAME_NOTE_NEEDS_NAME,
  REVIEW_NOTES_LEDE,
} from './writerCopy';

export const FROM_TALKS_AND_NOTES = MAKE_NOTES_FROM_CHATS_AND_DOCS;

export const CHECKLIST_LEDE = FROM_CHATS_AND_DOCS_LEDE;

export const REVIEW_LEDE = REVIEW_NOTES_LEDE;

export const THIS_BOOK_FLAG =
  'This offer looks like this book, not series. Accept to keep it at series, or skip.';

export const BOOK_ACCEPT_NOTE = BOOK_ACCEPT_NOTE_COPY;

export const CHARACTER_CARD_NOTE = CHARACTER_NOTE_SKETCH;

export const CHARACTER_DOSSIER_NOTE = CHARACTER_DOSSIER_NOTE_LINE;

export const CUSTOM_ACCEPT_NOTE = CUSTOM_ACCEPT_NOTE_COPY;

export const PLACE_ACCEPT_NOTE = PLACE_ACCEPT_NOTE_COPY;

export const PLOT_ACCEPT_NOTE = PLOT_ACCEPT_NOTE_COPY;

export const REVIEW_GROUP_ORDER = ['people', 'place', 'plot', 'book', 'custom'];

export const KIND_LABEL = {
  people: 'People',
  place: 'Place',
  plot: 'Plot',
  book: 'Books',
  custom: 'Custom',
};

export function offerKind(kind) {
  const raw = String(kind || '').trim().toLowerCase();
  if (raw === 'character' || raw === 'person' || raw === 'people') return 'people';
  if (raw === 'world' || raw === 'place') return 'place';
  if (raw === 'plot') return 'plot';
  if (raw === 'custom') return 'custom';
  if (raw === 'book') return 'book';
  return raw;
}

export function seriesOffersPath(projectId) {
  return projectId ? `/projects/${projectId}/series-offers` : '/projects';
}

export function isSeriesOffersPath(pathname) {
  return /^\/projects\/[^/]+\/series-offers\/?$/.test(pathname || '');
}

export function talkLabel(session, discoveryId) {
  if (!session) return 'Series chat';
  if (session.session_type === 'discovery' || session.id === discoveryId) {
    return SERIES_DISCOVERY_LABEL;
  }
  if ((session.guidance_mode || '') === 'focus') {
    const kind = (session.template || 'custom').replace(/^./, (ch) => ch.toUpperCase());
    return session.name || `Focus · ${kind}`;
  }
  return session.name || 'Explore';
}

export function unwrapSessions(res) {
  if (Array.isArray(res)) return res.filter(Boolean);
  if (Array.isArray(res?.data)) return res.data.filter(Boolean);
  if (Array.isArray(res?.sessions)) return res.sessions.filter(Boolean);
  return [];
}

export function unwrapImportBatches(res) {
  if (Array.isArray(res?.batches)) return res.batches;
  if (Array.isArray(res?.data?.batches)) return res.data.batches;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
}

export function sessionsFromSidebar(sidebar) {
  const items = Array.isArray(sidebar) ? sidebar : (sidebar?.items || []);
  const out = [];
  const seen = new Set();
  const push = (session) => {
    if (!session?.id || seen.has(session.id)) return;
    seen.add(session.id);
    out.push({
      id: session.id,
      name: session.name,
      description: session.description,
      guidance_mode: session.guidance_mode,
      template: session.template,
      session_type: session.session_type || null,
      message_count: session.message_count || 0,
    });
  };
  items.forEach((item) => {
    if (!item) return;
    if (item.type !== 'folder') push(item);
    (item.sessions || []).forEach(push);
  });
  return out;
}

export function mergeDiscoverySession(sessions, discovery, discoveryId) {
  const list = unwrapSessions(sessions);
  const id = discovery?.id || discoveryId;
  if (!id) return list;
  const extras = {
    id,
    session_type: 'discovery',
    name: discovery?.name || 'Series Discovery',
  };
  const index = list.findIndex((row) => row.id === id);
  if (index >= 0) {
    return list.map((row, i) => (
      i === index
        ? { ...row, ...extras, message_count: discovery?.message_count ?? row.message_count }
        : row
    ));
  }
  return [{
    guidance_mode: 'explore',
    message_count: discovery?.message_count || 0,
    description: discovery?.description || '',
    ...discovery,
    ...extras,
  }, ...list];
}

export async function loadTalkSessions(projectId, discoveryId, {
  listProject = (id) => sessionsApi.listProject(id),
  getProjectSidebar = (id) => sessionsApi.getProjectSidebar(id),
  getSession = (id) => sessionsApi.get(id),
} = {}) {
  let sessions = [];
  try {
    sessions = unwrapSessions(await listProject(projectId));
  } catch {
    sessions = [];
  }
  if (sessions.length === 0) {
    try {
      sessions = sessionsFromSidebar(await getProjectSidebar(projectId));
    } catch {
      sessions = [];
    }
  }
  const hasDiscovery = sessions.some((row) => (
    row.id === discoveryId || row.session_type === 'discovery'
  ));
  let discovery = null;
  if (discoveryId && !hasDiscovery) {
    try {
      discovery = await getSession(discoveryId);
    } catch {
      discovery = { id: discoveryId, session_type: 'discovery', name: 'Series Discovery' };
    }
  }
  return mergeDiscoverySession(sessions, discovery, discoveryId);
}

/**
 * Load series talks and uploaded note files independently. A notes
 * miss must not blank series talks. Series Discovery stays on the
 * list when the project already has that notebook (#784). Get Book
 * Ideas uses the same pile so uploaded files already on the
 * project appear in that picker (#796 / #800).
 */
export async function loadMakeCardsSources(projectId, discoveryId, {
  listProject,
  getProjectSidebar,
  getSession,
  listImports = (id) => documentImportApi.list(id),
} = {}) {
  const [talksResult, notesResult] = await Promise.allSettled([
    loadTalkSessions(projectId, discoveryId, { listProject, getProjectSidebar, getSession }),
    listImports(projectId),
  ]);
  const sessions = talksResult.status === 'fulfilled' ? talksResult.value : [];
  const batches = notesResult.status === 'fulfilled'
    ? unwrapImportBatches(notesResult.value)
    : [];
  const checklist = buildChecklist(sessions, batches, discoveryId);
  // Do not surface Failed to fetch when the talks are already listed.
  const error = checklist.talks.length === 0 && talksResult.status === 'rejected'
    ? talksResult.reason
    : null;
  return { ...checklist, error };
}

export function buildChecklist(sessions, batches, discoveryId) {
  const { discovery, explore, focus } = partitionSessions(sessions, discoveryId);
  const talks = [];
  if (discovery) {
    talks.push({
      id: discovery.id,
      kind: 'talk',
      label: talkLabel(discovery, discoveryId),
      detail: sessionCardSummary(discovery),
    });
  }
  [...explore, ...focus].forEach((session) => {
    talks.push({
      id: session.id,
      kind: 'talk',
      label: talkLabel(session, discoveryId),
      detail: sessionCardSummary(session),
    });
  });
  const notes = (batches || []).map((batch) => {
    const files = (batch.documents || [])
      .map((doc) => doc.original_filename || doc.filename)
      .filter(Boolean);
    return {
      id: batch.batch_id || batch.id,
      kind: 'note',
      label: files.join(', ') || 'Uploaded notes',
      detail: docListDetail(batch, 'Notes in the project pile.'),
      scopeNote: batch.scope_note || '',
    };
  }).filter((item) => item.id);
  return { talks, notes };
}

export function selectedIds(checked, items) {
  return (items || []).filter((item) => checked[item.id]).map((item) => item.id);
}

export function groupCandidates(candidates) {
  const groups = { people: [], place: [], plot: [], book: [], custom: [] };
  (candidates || []).forEach((candidate) => {
    const kind = offerKind(candidate.kind);
    if (kind === 'book' && !isRealBookName(candidate.title)) return;
    if (groups[kind]) groups[kind].push({ ...candidate, kind });
  });
  return groups;
}

export function ideaCardFromCandidate(items, candidate) {
  // One new book object. Timeline stores it; Series Notes reads the same id.
  const card = newIdeaCard(items);
  const title = (candidate?.title || '').trim();
  const statement = candidate?.statement || candidate?.proposition || '';
  return {
    ...card,
    title: title || card.title,
    proposition: clampProposition(statement),
    status: 'idea',
    story_id: null,
  };
}

/**
 * Make-cards / Discovery book-card accept (#678 / #709).
 * Writes one Timeline idea pin — the same object Series Notes reads.
 * Never starts a book. Never creates a story record.
 */
export function visibleCandidates(candidates) {
  return (candidates || [])
    .map((row) => ({ ...row, kind: offerKind(row.kind) }))
    .filter((row) => {
      if (row.kind === 'book') return isRealBookName(row.title);
      return REVIEW_GROUP_ORDER.includes(row.kind);
    });
}

export async function persistAcceptedBookIdea(projectId, candidate, {
  getBoard = (id) => seriesBookBoardApi.get(id),
  putBoard = (id, board) => seriesBookBoardApi.put(id, board),
} = {}) {
  const title = (candidate?.title || '').trim();
  if (!isRealBookName(title)) {
    throw new Error(BOOK_NAME_NOTE_NEEDS_NAME);
  }
  const board = await getBoard(projectId);
  const items = itemsFromBoard(board);
  const card = ideaCardFromCandidate(items, candidate);
  await putBoard(projectId, boardPayload(mergeTimelineItems(items, [...items, card])));
  return card;
}

export function snapshotCardFromCandidate(candidate) {
  const offer = offerKind(candidate?.kind);
  const statement = (candidate?.statement || '').trim();
  const title = (candidate?.title || '').trim();
  if (offer === 'place') {
    return {
      id: newCardId(),
      kind: 'world',
      name: title || 'World',
      description: statement,
      writeup: '',
    };
  }
  if (offer === 'plot') {
    return {
      id: newCardId(),
      kind: 'custom',
      name: title,
      description: statement,
      writeup: '',
    };
  }
  const kind = offer === 'custom' ? 'custom' : 'people';
  const rich = kind === 'people' && candidate?.depth === 'dossier';
  return {
    id: newCardId(),
    kind,
    name: title,
    description: statement,
    writeup: rich ? statement : '',
  };
}

export function destinationNote(candidate) {
  const kind = offerKind(candidate?.kind);
  if (kind === 'book') return BOOK_ACCEPT_NOTE;
  if (kind === 'place') return PLACE_ACCEPT_NOTE;
  if (kind === 'plot') return PLOT_ACCEPT_NOTE;
  if (kind === 'custom') return CUSTOM_ACCEPT_NOTE;
  if (candidate?.depth === 'dossier') return CHARACTER_DOSSIER_NOTE;
  return CHARACTER_CARD_NOTE;
}

export function looksLikeThisBook(candidate) {
  return candidate?.scope === 'this-book';
}
